import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function Testimonials() {
  const { t } = useTranslation();
  const TESTIMONIALS = [
    {
      quote: t("testimonials.quote1"),
      name: t("testimonials.name1"),
      role: t("testimonials.role1"),
      avatar:
        "https://api.iconify.design/solar:user-rounded-bold.svg?color=%23f97316", // replace with your avatar path
    },
    {
      quote: t("testimonials.quote2"),
      name: t("testimonials.name2"),
      role: t("testimonials.role2"),
      avatar:
        "https://api.iconify.design/solar:user-rounded-bold.svg?color=%23636b74",
    },
    {
      quote: t("testimonials.quote3"),
      name: t("testimonials.name3"),
      role: t("testimonials.role3"),
      avatar:
        "https://api.iconify.design/solar:user-rounded-bold.svg?color=%236b7280",
    },
  ];
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);

  // autoplay (respects reduced motion)
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    startAuto();
    return stopAuto;
  }, [idx]);

  const startAuto = () => {
    stopAuto();
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % TESTIMONIALS.length);
    }, 4500);
  };
  const stopAuto = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const goTo = (i) => setIdx((i + TESTIMONIALS.length) % TESTIMONIALS.length);
  const next = () => goTo(idx + 1);
  const prev = () => goTo(idx - 1);

  // keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // touch swipe
  const onTouchStart = (e) => {
    stopAuto();
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    const d = touchDeltaX.current;
    if (Math.abs(d) > 40) (d < 0 ? next : prev)();
    startAuto();
  };

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-800">
          {t("testimonials.title")}
        </h2>
        <p className="mt-2 text-sm sm:text-base text-slate-500">
          {t("testimonials.subtitle")}
        </p>

        {/* slider */}
        <div
          className="relative mt-8 sm:mt-10"
          onMouseEnter={stopAuto}
          onMouseLeave={startAuto}
        >
          {/* Card container */}
          <div
            className="mx-auto max-w-3xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            role="region"
            aria-roledescription="carousel"
            aria-label={t("testimonials.carouselAriaLabel")}
          >
            {/* slides */}
            <div
              className="relative"
              style={{ minHeight: 220 }}
            >
              {TESTIMONIALS.map((item, i) => {
                const isActive = i === idx;
                return (
                  <article
                    key={i}
                    className={`absolute inset-0 px-5 sm:px-7 md:px-8 py-6 sm:py-7 text-left transition-opacity duration-400 ease-out
                               ${isActive ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                    aria-hidden={!isActive}
                  >
                    {/* quote glyph */}
                    <div className="text-orange-500 mb-2">
                      {/* decorative quote icon */}
                      <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          d="M7.5 6C5.6 6 4 7.6 4 9.5S5.6 13 7.5 13c.3 0 .5 0 .8-.1C8 15.4 6.8 17.2 4 18v2c4.5-1 7-4.1 7-8.5C11 8.6 9.4 6 7.5 6Zm9 0C14.6 6 13 7.6 13 9.5s1.6 3.5 3.5 3.5c.3 0 .5 0 .8-.1-.3 2.5-1.5 4.3-4.3 5.1v2c4.5-1 7-4.1 7-8.5C20 8.6 18.4 6 16.5 6Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>

                    <p className="text-slate-700 leading-relaxed">
                      {item.quote}
                    </p>

                    {/* author */}
                    <div className="mt-5 flex items-center gap-3">
                      <img
                        src={item.avatar}
                        alt=""
                        className="h-7 w-7 rounded-full ring-1 ring-slate-200 object-contain"
                        loading="lazy"
                      />
                      <div className="text-sm">
                        <div className="font-semibold text-slate-800">{item.name}</div>
                        <div className="text-slate-500">{item.role}</div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {/* arrows */}
          <button
            onClick={prev}
            aria-label={t("testimonials.prevAriaLabel")}
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 items-center justify-center h-9 w-9 rounded-full bg-white/90 shadow ring-1 ring-slate-200 hover:bg-white"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
            </svg>
          </button>
          <button
            onClick={next}
            aria-label={t("testimonials.nextAriaLabel")}
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 items-center justify-center h-9 w-9 rounded-full bg-white/90 shadow ring-1 ring-slate-200 hover:bg-white"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
            </svg>
          </button>

          {/* dots */}
          <div className="mt-4 flex justify-center gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                aria-label={t("testimonials.goToSlide", { n: i + 1 })}
                onClick={() => goTo(i)}
                className={`h-2.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-orange-500" : "w-2.5 bg-slate-300"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
